import { Injectable, inject } from '@angular/core';
import {
  getDoc, doc, collection, setDoc, updateDoc,
  query, where, getDocs, arrayUnion, arrayRemove,
  deleteField,
} from 'firebase/firestore';

import { Account } from '../models/account.model';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';

export enum AccountErrorCodes {
  AccountNotFound = 'account-not-found',
  LicenseNotAccepted = 'license-not-accepted',
  UserNotFound = 'user-not-found',
  UserDataNotFound = 'user-data-not-found',
  UserNotLoggedIn = 'user-not-logged-in',
  Unknown = 'unknown',
}

export class AccountError extends Error {
  constructor(readonly code: AccountErrorCodes) {
    super(code);
    this.name = 'AccountError';
  }
}

const defaultAccount: Account = {
  id: '',
  name: 'Default Account',
  ownerId: '',
  vendorId: '',
  createdAt: new Date(),
};

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly userService = inject(UserService);

  private readonly currentAccountKey = 'currentAccount';
  private readonly DEFAULT_ACCOUNT_NAME = 'Default Account';

  getCurrentAccountIdFromSession(): string | null {
    return this.dataStore.getAuthStateProperty(this.currentAccountKey, false);
  }

  async setCurrentAccountInSession(accountId: string): Promise<void> {
    this.dataStore.setAuthStateProperty(this.currentAccountKey, accountId, false);
    await this.setCurrentAccountOnUser(accountId);
  }

  async deleteCurrentAccountInSession(): Promise<void> {
    this.dataStore.removeAuthStateProperty(this.currentAccountKey);
    await this.deleteCurrentAccountOnUser();
  }

  async deleteAccount(accountId: string | undefined): Promise<void> {
    throw new Error('Product accounts cannot be deleted');
  }

  private async setCurrentAccountOnUser(currentAccountId: string): Promise<void> {
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) throw new AccountError(AccountErrorCodes.UserNotFound);
    const userRef = doc(this.firebase.globalFirestore, 'users', currentUser.uid);
    await setDoc(userRef, { currentAccountId }, { merge: true });
  }

  private async deleteCurrentAccountOnUser(): Promise<void> {
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) throw new AccountError(AccountErrorCodes.UserNotFound);
    const userRef = doc(this.firebase.globalFirestore, 'users', currentUser.uid);
    await updateDoc(userRef, { currentAccountId: deleteField() });
  }

  clearCurrentAccount(): void {
    this.dataStore.removeAuthStateProperty(this.currentAccountKey);
  }

  getAccountRef(accountId: string) {
    return doc(this.firebase.firestore, 'developers', accountId);
  }

  async getAccount(accountId: string): Promise<Account> {
    if (!this.firebase.auth.currentUser) {
      throw new AccountError(AccountErrorCodes.UserNotLoggedIn);
    }

    const accountDoc = await getDoc(doc(this.firebase.firestore, 'developers', accountId));
    const data = accountDoc.data();
    if (!data || !data['name']) {
      throw new AccountError(AccountErrorCodes.AccountNotFound);
    }

    return {
      id: accountId,
      name: data['name'],
      ownerId: data['ownerId'],
      vendorId: data['vendorId'],
      organizationId: data['organizationId'],
      authorizedDomains: data['authorizedDomains'],
      features: data['features'],
      createdAt: data['createdAt'],
      creditBalance: data['creditBalance'],
      product: data['product'],
    };
  }

  async getCurrentAccount(forceFetch = false): Promise<Account> {
    const inMemoryAccount = this.dataStore.getProperty('currentAccount');
    if (!forceFetch && inMemoryAccount) {
      return inMemoryAccount;
    }

    await this.firebase.auth.authStateReady();
    if (!this.firebase.auth.currentUser) {
      throw new AccountError(AccountErrorCodes.UserNotLoggedIn);
    }

    let accountId = this.getCurrentAccountIdFromSession();
    if (!accountId) {
      accountId = await this.userService.getCurrentAccountId();
    }

    let currentAccount: Account;
    try {
      currentAccount = await this.getAccount(accountId);
    } catch {
      try {
        const defaultAccountId = await this.userService.getDefaultAccountId();
        currentAccount = await this.getAccount(defaultAccountId);
      } catch {
        currentAccount = defaultAccount;
      }
    }

    this.setCurrentAccountInSession(currentAccount.id);
    this.dataStore.updateState({ currentAccount });

    if (currentAccount.vendorId === '') {
      // Product accounts are provisioned by the backend only (never from the browser).
      throw new AccountError(AccountErrorCodes.AccountNotFound);
    }

    return currentAccount;
  }

  async getCurrentContext(): Promise<'organization' | 'personal'> {
    const currentAccount = await this.getCurrentAccount();
    return currentAccount.organizationId ? 'organization' : 'personal';
  }

  async updateAccountName(name: string) {
    let currentAccount = this.dataStore.getProperty('currentAccount');
    if (!currentAccount) {
      currentAccount = await this.getCurrentAccount();
    }

    await setDoc(doc(this.firebase.firestore, 'developers', currentAccount.id), { name }, { merge: true });
    this.dataStore.updateState({ currentAccount: { ...currentAccount, name } });
  }

  async renameAccount(accountId: string, name: string) {
    await setDoc(doc(this.firebase.firestore, 'developers', accountId), { name }, { merge: true });
  }

  async setCurrentAccount(accountId: string) {
    const account = await this.getAccount(accountId);
    await this.updateAccountInState(account);
  }

  async updateAccountInState(account: Account) {
    await this.setCurrentAccountInSession(account.id);
    this.dataStore.updateState({ currentAccount: account });
  }

  async listAccountsByOrganization(organizationId: string): Promise<Account[]> {
    const accountsRef = collection(this.firebase.firestore, 'developers');
    const q = query(accountsRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return [];

    const accounts = querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data['name'],
        ownerId: data['ownerId'],
        vendorId: data['vendorId'],
        organizationId: data['organizationId'],
        authorizedDomains: data['authorizedDomains'],
        features: data['features'],
        createdAt: data['createdAt'],
        product: data['product'],
      };
    });

    return accounts;
  }

  async addAuthorizedDomain(domain: string) {
    const currentAccount = await this.getCurrentAccount();
    await setDoc(
      doc(this.firebase.firestore, 'developers', currentAccount.id),
      { authorizedDomains: arrayUnion(domain) },
      { merge: true },
    );
    this.dataStore.updateState({ currentAccount });
  }

  async removeAuthorizedDomain(domain: string) {
    const currentAccount = await this.getCurrentAccount();
    await setDoc(
      doc(this.firebase.firestore, 'developers', currentAccount.id),
      { authorizedDomains: arrayRemove(domain) },
      { merge: true },
    );
  }

  async addAuthorizedDomainToAccount(accountId: string, domain: string) {
    await setDoc(
      doc(this.firebase.firestore, 'developers', accountId),
      { authorizedDomains: arrayUnion(domain) },
      { merge: true },
    );
  }

  async removeAuthorizedDomainFromAccount(accountId: string, domain: string) {
    await setDoc(
      doc(this.firebase.firestore, 'developers', accountId),
      { authorizedDomains: arrayRemove(domain) },
      { merge: true },
    );
  }

}
